import { useWindowsPref } from "@/lib/windows-pref";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import {
  AppWindow,
  Columns2,
  Eye,
  EyeOff,
  LogOut,
  Palette,
  PanelBottom,
  PanelLeft,
  PanelLeftClose,
  PanelRight,
  PanelTop,
  Plus,
  Rows2,
  Minus,
  ChevronsUpDown,
  ServerCrash,
  X,
  LayoutTemplate,
  Save,
} from "lucide-react";
import { Hint, TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { BoardSidebar, TOGGLE_SIDEBAR_EVENT, type SidebarView } from "@/components/board-sidebar";
import { CommandPalette, type PaletteAction } from "@/components/command-palette";
import { BottomTabs } from "@/components/bottom-tabs";
import { TopBar } from "@/components/top-bar";
import { TaskDialog } from "@/components/task-dialog";
import { CreateBoardDialog } from "@/components/create-board-dialog";
import { HOME_VIEW, ShellContext, ViewPane, viewTitle, type Go, type GoOptions, type Shell } from "@/components/view-pane";
import { WorkspaceView } from "@/components/workspace";
import { LayoutsMenu, WindowsMenu, layoutViewExists, viewIcon } from "@/components/windows-menu";
import { useLayoutActions, useLayouts } from "@/hooks/use-layouts";
import { usePwaUpdateToast } from "@/lib/pwa";
import { isDarkTheme, setThemePref, THEMES, themeName, useTheme } from "@/lib/theme";
import { isPaletteShortcut } from "@/lib/palette";
import { focusPrimaryInput, isFocusInputShortcut } from "@/lib/focus-input";
import { useAgents } from "@/components/agents-view";
import { WaitingContext, useWaitingState } from "@/lib/waiting-context";
import { waitingAgentsTotal } from "@/lib/waiting";
import { focusAgentMessage } from "@/lib/agent-focus";
import { onServiceWorkerLink, takeDeepLinkFromLocation, type DeepLink } from "@/lib/deep-link";
import { useLiveEvent } from "@/lib/live";
import { useBoards, useNow } from "@/hooks/use-boards";
import { useKeyboard } from "@/hooks/use-keyboard";
import { api, ApiError } from "@/lib/api";
import { useAuth, useCan } from "@/lib/auth";
import { previewText } from "@/lib/preview-text";
import { navigateBack, onBackView, startBackStack, type BackStackView } from "@/lib/back-stack";
import { isEditableTarget, takeOpenBeside, trackOpenBeside } from "@/lib/window-intents";
import { dockAt } from "@/lib/drop-zone";
import {
  agentChatNear,
  closeWindow,
  canMinimize,
  isMinimized,
  toggleMinimize,
  findWindow,
  focus,
  focusedWindow,
  openAgentChat,
  openEmptyWindow,
  openView,
  replaceTab,
  showAgentList,
  parseWorkspace,
  serializeWorkspace,
  setNav,
  setView,
  viewKey,
  windowCount,
  windows,
  WORKSPACE_STORAGE_KEY,
  applyLayout,
  layoutFromWorkspace,
  setActiveLayout,
  type Dock,
  type Workspace,
  cycleLayoutId,
  layoutCycleKey,
} from "@/lib/workspace";
import { type BoardDetail, type SavedLayoutNode } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * The view the app was on before windows: still written (the focused window's), so a
 * build without windows opens where this one was, and read once to migrate.
 */
const VIEW_STORAGE_KEY = "automation.view";

const SHORTCUT_VIEWS: Record<string, SidebarView> = {
  status: { kind: "status" },
  apps: { kind: "apps" },
  agents: { kind: "agents" },
  // The old "Talk to an agent" shortcut (removed with talkback) still opens Agents on an installed app.
  voice: { kind: "agents" },
  queue: { kind: "queue" },
  mine: { kind: "queue" },
  waiting: { kind: "waiting" },
  projects: { kind: "projects" },
  changelog: { kind: "changelog" },
  terminal: { kind: "terminal" },
  settings: { kind: "settings" },
  archive: { kind: "settings" },
};

/** The view a home-screen shortcut asked for, once: the parameter is dropped from the URL. */
const viewFromShortcut = (): SidebarView | null => {
  const url = new URL(window.location.href);
  const name = url.searchParams.get("view");
  if (!name) return null;
  url.searchParams.delete("view");
  window.history.replaceState(window.history.state, "", url.toString());
  return SHORTCUT_VIEWS[name] ?? null;
};
const LAST_BOARD_STORAGE_KEY = "automation.lastBoard";
const WIDE_QUERY = "(min-width: 1024px)";
/**
 * Where the bottom tab bar replaces the sidebar: anything narrower than xl, and a
 * touch screen up to 1400px so an iPad Pro in landscape (1366) gets tabs too. A
 * tablet is a thumb-driven device at any width; a desktop window is not.
 */
const TABS_QUERY = "(max-width: 1279.98px), (pointer: coarse) and (max-width: 1400px)";

/** Whether a media query matches, following it as a tablet rotates or a window resizes. */
function useMedia(source: string, fallback: boolean): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia?.(source).matches ?? fallback);
  useEffect(() => {
    const query = window.matchMedia?.(source);
    if (!query) return;
    const update = () => setMatches(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, [source]);
  return matches;
}

/**
 * The views back walks through from `view` — the focused window's: home, then the
 * tab it sits on, then the detail. A board's parent is the Boards list only where that list exists (the
 * tab layout); in the sidebar a board is a place of its own, one back from home.
 */
function backStackFor(view: SidebarView, nav: "sidebar" | "tabs"): BackStackView[] {
  const stack: SidebarView[] = [HOME_VIEW];
  if (view.kind === "board" && nav === "tabs") stack.push({ kind: "boards" });
  if (view.kind === "agents" && view.agentId) stack.push({ kind: "agents" });
  if (viewKey(view) !== viewKey(HOME_VIEW)) stack.push(view);
  return stack.map((entry) => ({ key: viewKey(entry), view: entry }));
}

/** Whether the viewport is laptop-wide — wide enough for columns side by side, outside a window. */
const useWide = () => useMedia(WIDE_QUERY, true);

/** The stored workspace, or one window on the view stored before windows; a shortcut's view opens in it. */
function initialWorkspace(): Workspace {
  let stored: string | null = null;
  let legacy: string | null = null;
  try {
    stored = localStorage.getItem(WORKSPACE_STORAGE_KEY);
    legacy = localStorage.getItem(VIEW_STORAGE_KEY);
  } catch {
    /* private mode: start fresh */
  }
  const ws = parseWorkspace(stored, legacy);
  // An installed app's shortcuts (the manifest's `shortcuts`) open `/?view=…`.
  const shortcut = viewFromShortcut();
  return shortcut ? openView(ws, shortcut, { kind: "tab", targetId: ws.focusedId }) : ws;
}

trackOpenBeside();

/** Sidebar on a desktop; the bottom tab bar on a phone and on every iPad, either way up. */
function useNavLayout(): "sidebar" | "tabs" {
  const small = useMedia(TABS_QUERY, false);
  const [windowsOn] = useWindowsPref();
  return small || !windowsOn ? "tabs" : "sidebar";
}

export default function App() {
  usePwaUpdateToast();
  const { boards, archivedBoards, allBoards, users, projects, loading, error, refresh, remoteChangeAt } = useBoards();
  const now = useNow(30_000);
  const nav = useNavLayout();
  const viewportWide = useWide();
  const { resolved: theme, pref: themePref } = useTheme();
  const { logout } = useAuth();
  const keyboardOpen = useKeyboard();
  const phone = useMedia("(max-width: 767.98px)", false);
  const canEdit = useCan("member");
  const canManageAgents = useCan("admin");
  const [newAgentRequest, setNewAgentRequest] = useState(0);
  const [newProjectRequest, setNewProjectRequest] = useState(0);
  const [newTaskRequest, setNewTaskRequest] = useState(0);
  const [connectRequest, setConnectRequest] = useState(0);
  /**
   * The desktop window a request above is for. Windows get none of them otherwise
   * (the top bar that makes them is the tab layout's), so the palette's New card /
   * New agent aims one at the focused window for a single render: long enough for
   * the view to see the count move, never left set for a window that mounts later.
   */
  const [requestWindow, setRequestWindow] = useState<string | null>(null);
  useEffect(() => {
    if (requestWindow) setRequestWindow(null);
  }, [requestWindow]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // ⌘K / Ctrl+K from anywhere — a text field included, which every other key here
  // leaves alone — and before the browser takes Ctrl+K for its address bar.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.repeat) return;
      // ⌘⇧K / Ctrl+Shift+K: the cursor into the focused window's composer (or an open sheet's).
      if (isFocusInputShortcut(event)) {
        event.preventDefault();
        event.stopPropagation();
        focusPrimaryInput(document, navRef.current === "sidebar" ? wsRef.current.focusedId : null);
        return;
      }
      if (!isPaletteShortcut(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setPaletteOpen((open) => !open);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  /**
   * The desktop workspace (`lib/workspace.ts`): windows tiled in a tree, each with a
   * view. The tab layout shows only the focused window, so a phone and a desktop
   * reading the same storage agree on where the user is.
   */
  const [ws, setWs] = useState<Workspace>(initialWorkspace);
  const wsRef = useRef(ws);
  const view = focusedWindow(ws).view;

  // History is the back stack (`lib/back-stack.ts`), and it follows the focused
  // window: taken over once, on the view the app opened on.
  startBackStack(backStackFor(view, nav));
  const navRef = useRef(nav);
  navRef.current = nav;

  /**
   * Every change to the workspace goes through here. When the focused window ends
   * up on another view, history follows it — into a detail pushes, sideways
   * replaces, as with one view — while opening, closing, moving and resizing
   * windows add nothing of their own: their undo is the Closed shelf.
   */
  const apply = useCallback((op: (ws: Workspace) => Workspace, options?: { history?: boolean }) => {
    const before = wsRef.current;
    const next = op(before);
    if (next === before) return;
    wsRef.current = next;
    setWs(next);
    const nextView = focusedWindow(next).view;
    if (options?.history !== false && viewKey(focusedWindow(before).view) !== viewKey(nextView)) {
      navigateBack(backStackFor(nextView, navRef.current));
    }
  }, []);

  /**
   * A window's own `go`. On the desktop a plain click opens a new tab in that window,
   * beside its active one (already open anywhere, it is shown where it is);
   * Ctrl/Cmd-click opens it beside, as a window. `instead` is a view moving on
   * because it cannot be shown: its own tab is replaced, never the one on screen. The
   * tab layout replaces. Cached per window so the views' effects that depend on it do
   * not re-run every render.
   */
  const goCache = useRef(new Map<string, Go>());
  const goFor = useCallback(
    (windowId: string) => {
      let go = goCache.current.get(windowId);
      if (!go) {
        go = (next: SidebarView, options?: GoOptions) => {
          const sidebar = navRef.current === "sidebar";
          if (options?.instead) {
            const from = options.instead;
            apply((current) =>
              sidebar ? replaceTab(current, windowId, from, next) : openView(focus(current, windowId), next, { kind: "replace" }),
            );
            return;
          }
          const here = sidebar ? findWindow(wsRef.current, windowId)?.view : undefined;
          // An empty window becomes what was picked in it, even when that is open elsewhere.
          if (here?.kind === "empty") {
            takeOpenBeside();
            apply((current) => setView(focus(current, windowId), windowId, next));
            return;
          }
          // On the desktop the Agents list and a chat are windows of their own: an
          // agent picked in the list opens beside it, the list asked for from a chat
          // opens beside that, so neither replaces the other.
          if (here?.kind === "agents" && next.kind === "agents" && Boolean(here.agentId) !== Boolean(next.agentId)) {
            const beside = takeOpenBeside();
            apply((current) =>
              next.agentId ? openAgentChat(current, windowId, next.agentId, { beside }) : showAgentList(current, windowId),
            );
          } else if (sidebar && takeOpenBeside()) {
            apply((current) => openView(current, next, { kind: "split", targetId: windowId, zone: "right" }));
          } else {
            apply((current) => openView(focus(current, windowId), next, sidebar ? { kind: "tab", targetId: windowId } : { kind: "replace" }));
          }
        };
        goCache.current.set(windowId, go);
      }
      return go;
    },
    [apply],
  );
  /** The focused window's `go`: the navigation, a notification, a toast. */
  const go = useCallback((next: SidebarView) => goFor(wsRef.current.focusedId)(next), [goFor]);
  /** Open here: in place of the focused window's view, the one way a click still replaces on the desktop. */
  const goHere = useCallback((next: SidebarView) => apply((current) => openView(current, next, { kind: "replace" })), [apply]);
  // A back (or forward) that lands on another view shows it in the focused window; history is already there.
  useEffect(() => onBackView((next) => apply((current) => setView(current, current.focusedId, next as SidebarView), { history: false })), [apply]);

  /** The board the Boards tab brings back — the last one opened on this device. */
  const [lastBoardId, setLastBoardId] = useState<string | null>(() => localStorage.getItem(LAST_BOARD_STORAGE_KEY));
  // Saved a beat after it settles, not on every change — a drag or a run of tab
  // switches writes once — and at once when the page goes away.
  useEffect(() => {
    const save = () => {
      try {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, serializeWorkspace(ws));
        localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(view));
      } catch {
        /* private mode: the layout lasts the session */
      }
    };
    const timer = window.setTimeout(save, 250);
    window.addEventListener("pagehide", save);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pagehide", save);
    };
  }, [ws, view]);
  useEffect(() => {
    if (view.kind !== "board") return;
    localStorage.setItem(LAST_BOARD_STORAGE_KEY, view.boardId);
    setLastBoardId(view.boardId);
  }, [view]);

  const activeBoard = useMemo(
    () => (view.kind === "board" ? allBoards.find((detail) => detail.board.id === view.boardId) ?? null : null),
    [allBoards, view],
  );

  /**
   * Cards waiting on the user's answer, fetched once for the whole app and shared
   * through context: the cards, the board header, the navigation, the queue and
   * the task sheet all read the same list. Empty, and never fetched, below admin.
   */
  const waiting = useWaitingState();
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [boardDialogOpen, setBoardDialogOpen] = useState(false);
  const fail = useCallback((message: string) => toast.error(message), []);

  /**
   * An agent turning to the user is announced wherever they are: the chat it was
   * asked in may not be the one open, and a question nobody sees is an agent that
   * waits for ever. Only a new, open ask — not the update that closes it.
   */
  const { agents } = useAgents();
  // The same open questions the list's pills and the chat's bar show, so the badge agrees with them.
  const waitingAgents = waitingAgentsTotal(agents);
  /** An agent's chat, scrolled to one message when given — a question, from wherever it was seen. */
  const openAgent = useCallback(
    (agentId: string, messageId?: string | null) => {
      if (messageId) focusAgentMessage(agentId, messageId);
      go({ kind: "agents", agentId });
    },
    [go],
  );
  // A tapped notification: opened at its URL (no window was open), or told by the
  // service worker which window to move (lib/deep-link.ts).
  useEffect(() => {
    const applyLink = (link: DeepLink) => {
      if (link.kind === "agent") openAgent(link.agentId, link.messageId);
      else if (link.kind === "task") setOpenTaskId(link.taskId);
      else {
        const target = SHORTCUT_VIEWS[link.view];
        if (target) go(target);
      }
    };
    const initial = takeDeepLinkFromLocation();
    if (initial) applyLink(initial);
    return onServiceWorkerLink(applyLink);
  }, [openAgent, go]);
  const announced = useRef(new Set<string>());
  useLiveEvent((event) => {
    if (event.type !== "agent.message") return;
    const message = event.message;
    if ((message.kind !== "question" && message.kind !== "suggestion") || message.data?.status !== "open") return;
    if (announced.current.has(message.id)) return;
    announced.current.add(message.id);
    // Not when that chat is on screen — in any window — and the app has focus.
    const shown = (nav === "tabs" ? [focusedWindow(ws)] : windows(ws)).some(
      (window) => window.view.kind === "agents" && window.view.agentId === message.agentId,
    );
    if (shown && document.hasFocus()) return;
    const agent = agents.find((candidate) => candidate.id === message.agentId);
    const name = agent?.name ?? "An agent";
    toast(message.kind === "question" ? `${name} is asking you` : `${name} has a suggestion`, {
      description: previewText(message.body).slice(0, 140),
      duration: message.kind === "question" ? 20_000 : 8_000,
      action: { label: "Open", onClick: () => openAgent(message.agentId, message.id) },
    });
  });

  /**
   * Takes the board it acts on rather than reading the selected one: the same
   * action is reachable from the board menu, an expired row in the sidebar and
   * the archive page, and only the first of those is looking at the open board.
   */
  const setBoardArchived = useCallback(
    async (detail: BoardDetail, archived: boolean) => {
      try {
        await api.updateBoard(detail.board.id, { archived });
        await refresh();
        toast.success(archived ? "Board archived" : "Board restored", {
          description: archived ? "Nothing was deleted — it is under Settings → Archive." : undefined,
        });
      } catch (cause) {
        fail(cause instanceof ApiError ? cause.message : `Could not ${archived ? "archive" : "restore"} the board`);
      }
    },
    [refresh, fail],
  );

  /** A window showing the deleted board moves on by itself (`ViewPane`), wherever it is. */
  const removeBoard = useCallback(
    async (detail: BoardDetail) => {
      if (!window.confirm(`Delete "${detail.board.name}" and all ${detail.stats.total} of its tasks?`)) return;
      try {
        await api.deleteBoard(detail.board.id);
        await refresh();
        toast.success("Board deleted");
      } catch (cause) {
        fail(cause instanceof ApiError ? cause.message : "Could not delete the board");
      }
    },
    [refresh, fail],
  );

  /** A palette choice: the focused window, or beside it (the desktop workspace only). */
  const paletteGo = useCallback(
    (target: SidebarView, beside: boolean) => {
      takeOpenBeside(); // the click that chose it already said which, here
      if (beside && navRef.current === "sidebar") {
        // Asked for a new window by name, so it is one even when the view is open elsewhere.
        apply((current) =>
          openView(current, target, { kind: "split", targetId: current.focusedId, zone: "right" }, { duplicate: true }),
        );
      } else go(target);
    },
    [apply, go],
  );
  const toggleNavRef = useRef<() => void>(() => {});
  const toFocusedWindow = useCallback(() => {
    if (navRef.current === "sidebar") setRequestWindow(wsRef.current.focusedId);
  }, []);

  const live = remoteChangeAt !== null && Date.now() - remoteChangeAt < 6000;

  /** Up from a board to the list: a back, when the board was entered from it. */
  const backToBoards = useCallback(() => go({ kind: "boards" }), [go]);

  /**
   * One agent's chat on a phone is a conversation screen, as in a messaging app:
   * its own header (with the back button) replaces the top bar, and the tab bar
   * steps aside so the composer has the bottom of the screen.
   */
  const inAgentChat = nav === "tabs" && phone && view.kind === "agents" && Boolean(view.agentId);

  /** The current view's own actions, for the tab layout's top bar. */
  const topBarActions =
    view.kind === "board" && activeBoard && canEdit && !activeBoard.board.archived && activeBoard.board.kind !== "canvas" ? (
      <Button variant="ghost" size="icon" aria-label="New task" onClick={() => setNewTaskRequest((count) => count + 1)}>
        <Plus />
      </Button>
    ) : view.kind === "boards" && canEdit ? (
      <Button variant="ghost" size="icon" aria-label="New board" onClick={() => setBoardDialogOpen(true)}>
        <Plus />
      </Button>
    ) : view.kind === "projects" && canManageAgents ? (
      <Button variant="ghost" size="icon" aria-label="Add project" onClick={() => setNewProjectRequest((count) => count + 1)}>
        <Plus />
      </Button>
    ) : view.kind === "agents" && canManageAgents ? (
      <Button variant="ghost" size="icon" aria-label="New agent" onClick={() => setNewAgentRequest((count) => count + 1)}>
        <Plus />
      </Button>
    ) : null;

  /** What the palette can do, offered only where the UI would offer it. */
  // A canvas has no cards of its own, so it offers no "New card" here.
  const focusedBoard = activeBoard && canEdit && !activeBoard.board.archived && activeBoard.board.kind !== "canvas" ? activeBoard : null;
  const layouts = useLayouts();
  const layoutActions = useLayoutActions();
  const activeLayout = ws.layoutId ? layouts.find((layout) => layout.id === ws.layoutId) ?? null : null;
  const paletteActions = useMemo<PaletteAction[]>(() => {
    const list: PaletteAction[] = [];
    if (focusedBoard) {
      list.push({
        id: "new-card",
        title: "New card",
        subtitle: `On ${focusedBoard.board.name}`,
        icon: <Plus />,
        hint: "N",
        keywords: ["task", "add", "create"],
        run: () => {
          toFocusedWindow();
          setNewTaskRequest((count) => count + 1);
        },
      });
    }
    if (canEdit) {
      list.push({ id: "new-board", title: "New board", icon: <Plus />, keywords: ["create", "add"], run: () => setBoardDialogOpen(true) });
    }
    if (canManageAgents) {
      list.push({
        id: "new-agent",
        title: "New agent",
        icon: <Plus />,
        keywords: ["create", "add", "session"],
        run: () => {
          go({ kind: "agents" });
          toFocusedWindow();
          setNewAgentRequest((count) => count + 1);
        },
      });
    }
    if (nav === "sidebar") {
      const docked = ws.nav.dock;
      if (!ws.nav.hidden) {
        list.push({
          id: "toggle-sidebar",
          title: docked === "top" || docked === "bottom" ? "Hide navigation" : "Toggle sidebar",
          icon: <PanelLeftClose />,
          hint: "[",
          keywords: ["collapse", "expand", "rail", "navigation"],
          run: () => window.dispatchEvent(new Event(TOGGLE_SIDEBAR_EVENT)),
        });
      }
      list.push({
        id: "nav-visibility",
        title: ws.nav.hidden ? "Show navigation" : "Hide navigation",
        icon: ws.nav.hidden ? <Eye /> : <EyeOff />,
        keywords: ["sidebar"],
        run: () => toggleNavRef.current(),
      });
      list.push({
        id: "new-empty-window",
        title: "New empty window",
        icon: <AppWindow />,
        hint: "Alt+N",
        keywords: ["window", "blank", "open"],
        run: () => apply(openEmptyWindow),
      });
      list.push({
        id: "split-right",
        title: "Split right",
        icon: <Columns2 />,
        hint: "Alt+\\",
        keywords: ["window", "side by side"],
        run: () =>
          apply((current) =>
            openView(current, focusedWindow(current).view, { kind: "split", targetId: current.focusedId, zone: "right" }, { duplicate: true }),
          ),
      });
      list.push({
        id: "split-below",
        title: "Split below",
        icon: <Rows2 />,
        hint: "Alt+Shift+\\",
        keywords: ["window"],
        run: () =>
          apply((current) =>
            openView(current, focusedWindow(current).view, { kind: "split", targetId: current.focusedId, zone: "bottom" }, { duplicate: true }),
          ),
      });
      if (windowCount(ws) > 1) {
        const barred = isMinimized(ws, ws.focusedId);
        list.push({
          id: "minimise-window",
          title: barred ? "Restore window" : "Minimise window",
          icon: barred ? <ChevronsUpDown /> : <Minus />,
          hint: "Alt+M",
          keywords: ["window", "minimize", "collapse", "title bar", "restore"],
          run: () => {
            if (!barred && !canMinimize(ws, ws.focusedId)) toast("The last window stays open", { id: "minimise-last" });
            else apply((current) => toggleMinimize(current, current.focusedId));
          },
        });
        list.push({
          id: "close-window",
          title: "Close window",
          icon: <X />,
          hint: "Alt+W",
          keywords: ["window"],
          run: () => apply((current) => closeWindow(current, current.focusedId, Date.now())),
        });
      }
      if (activeLayout && canEdit) {
        // The Windows menu's Save: write these windows back to the layout they came from.
        list.push({
          id: "layout-save",
          title: `Save layout “${activeLayout.name}”`,
          subtitle: "Overwrite it with these windows",
          icon: <Save />,
          keywords: ["windows", "update", "layout"],
          run: () => {
            const { layout, nav: navNow } = layoutFromWorkspace(ws);
            void layoutActions
              .run({ kind: "replace", layoutId: activeLayout.id, name: activeLayout.name, layout: layout as SavedLayoutNode, nav: activeLayout.nav ? navNow : null })
              .then(() => apply((current) => setActiveLayout(current, activeLayout.id)))
              .catch(() => undefined);
          },
        });
      }
      for (const saved of layouts) {
        list.push({
          id: `layout-${saved.id}`,
          title: `Layout: ${saved.name}`,
          subtitle: "Apply this saved layout",
          icon: <LayoutTemplate />,
          keywords: ["windows", "arrange", "apply", "saved"],
          run: () => apply((current) => applyLayout(current, saved, (view) => layoutViewExists(view, allBoards, agents))),
        });
      }
      for (const [dockTo, label, icon] of [
        ["left", "Left", <PanelLeft key="l" />],
        ["right", "Right", <PanelRight key="r" />],
        ["top", "Top", <PanelTop key="t" />],
        ["bottom", "Bottom", <PanelBottom key="b" />],
      ] as const) {
        if (dockTo === docked && !ws.nav.hidden) continue;
        list.push({
          id: `dock-${dockTo}`,
          title: `Dock navigation ${label.toLowerCase()}`,
          icon,
          keywords: ["sidebar", "move"],
          run: () => apply((current) => setNav(current, { dock: dockTo, hidden: false })),
        });
      }
    }
    for (const option of THEMES) {
      list.push({
        id: `theme-${option.id}`,
        title: `Theme: ${themeName(option.id)}`,
        subtitle: themePref === option.id ? "Current" : option.hint,
        icon: <Palette />,
        keywords: ["appearance", "colour", "color", option.appearance],
        run: () => setThemePref(option.id),
      });
    }
    list.push({ id: "sign-out", title: "Sign out", icon: <LogOut />, keywords: ["log out", "logout"], run: () => void logout() });
    return list;
  }, [focusedBoard, canEdit, canManageAgents, nav, ws, layouts, activeLayout, layoutActions.run, allBoards, agents, themePref, logout, go, apply, toFocusedWindow]);

  const titleOf = useCallback((target: SidebarView) => viewTitle(target, allBoards, agents), [allBoards, agents]);

  // What every window reads. Memoised, with actions that never change identity: a new
  // object each render re-rendered every window, every tab and every card on every
  // focus change, drag step and refetch anywhere (tsk_b4mj5vp3's profile).
  const shellLatest = useRef({ refresh, setBoardArchived, removeBoard });
  shellLatest.current = { refresh, setBoardArchived, removeBoard };
  const shellActions = useMemo(
    () => ({
      openTask: (taskId: string) => setOpenTaskId(taskId),
      refresh: () => void shellLatest.current.refresh(),
      setBoardArchived: (detail: BoardDetail, archived: boolean) => void shellLatest.current.setBoardArchived(detail, archived),
      removeBoard: (detail: BoardDetail) => void shellLatest.current.removeBoard(detail),
      createBoard: () => setBoardDialogOpen(true),
    }),
    [],
  );
  const shell = useMemo<Shell>(
    () => ({
      boards,
      archivedBoards,
      allBoards,
      users,
      projects,
      agents,
      loading,
      now,
      nav,
      viewportWide,
      lastBoardId,
      ...shellActions,
      newAgentRequest,
      newProjectRequest,
      newTaskRequest,
      connectRequest,
    }),
    [boards, archivedBoards, allBoards, users, projects, agents, loading, now, nav, viewportWide, lastBoardId, shellActions, newAgentRequest, newProjectRequest, newTaskRequest, connectRequest],
  );

  /* ---- the navigation's dock: drag its logo to an edge of the screen ---- */
  const dock: Dock = ws.nav.dock;
  const navHidden = nav === "sidebar" && ws.nav.hidden;
  const [dockPreview, setDockPreview] = useState<Dock | null>(null);
  const startDockDrag = useCallback(
    (event: React.PointerEvent) => {
      if (event.button !== 0) return;
      event.preventDefault();
      const startX = event.clientX;
      const startY = event.clientY;
      let active = false;
      let target: Dock | null = null;
      const onMove = (move: PointerEvent) => {
        if (!active && Math.hypot(move.clientX - startX, move.clientY - startY) < 8) return;
        active = true;
        target = dockAt({ width: window.innerWidth, height: window.innerHeight }, move.clientX, move.clientY);
        setDockPreview(target);
      };
      const done = (commit: boolean) => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        setDockPreview(null);
        const landed = target;
        if (commit && active && landed) apply((current) => setNav(current, { dock: landed }));
      };
      const onUp = () => done(true);
      const onCancel = () => done(false);
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
    },
    [apply],
  );
  const toggleNav = useCallback(() => apply((current) => setNav(current, { hidden: !current.nav.hidden })), [apply]);
  toggleNavRef.current = toggleNav;
  // With the navigation hidden its own `[` is gone with it; this brings it back.
  useEffect(() => {
    if (!navHidden) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || event.key !== "[" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.isContentEditable || target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.tagName === "SELECT") return;
      event.preventDefault();
      toggleNav();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navHidden, toggleNav]);

  const windowsMenu = (
    trigger: React.ReactElement,
    side: "right" | "left" | "top" | "bottom",
    held?: { open: boolean; onOpenChange: (open: boolean) => void },
  ) => (
    <WindowsMenu ws={ws} apply={apply} titleOf={titleOf} boards={boards} allBoards={allBoards} agents={agents} canAwait={waiting.enabled} side={side} {...held}>
      {trigger}
    </WindowsMenu>
  );
  const layoutsMenu = (trigger: React.ReactElement, side: "right" | "left" | "top" | "bottom", hint?: string) => (
    <LayoutsMenu ws={ws} apply={apply} titleOf={titleOf} boards={boards} allBoards={allBoards} agents={agents} canAwait={waiting.enabled} side={side} hint={hint}>
      {trigger}
    </LayoutsMenu>
  );

  /* ---- Alt+L / Alt+Shift+L: the next or previous saved layout, its name flashed as a caption ---- */
  const [layoutCaption, setLayoutCaption] = useState<{ name: string; at: number } | null>(null);
  const cycleRef = useRef({ layouts, layoutId: ws.layoutId, allBoards, agents, apply, enabled: nav === "sidebar" });
  cycleRef.current = { layouts, layoutId: ws.layoutId, allBoards, agents, apply, enabled: nav === "sidebar" };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const current = cycleRef.current;
      if (event.defaultPrevented || !current.enabled) return;
      const step = layoutCycleKey({
        code: event.code,
        altKey: event.altKey,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        editable: isEditableTarget(event.target),
      });
      if (!step) return;
      // Claimed even with nothing to switch to, so Option+L never falls through to anything else.
      event.preventDefault();
      if (event.repeat) return;
      const id = cycleLayoutId(current.layouts.map((layout) => layout.id), current.layoutId, step);
      const saved = id ? current.layouts.find((layout) => layout.id === id) : null;
      if (!saved) return;
      current.apply((ws) => applyLayout(ws, saved, (view) => layoutViewExists(view, current.allBoards, current.agents)));
      setLayoutCaption({ name: saved.name, at: Date.now() });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!layoutCaption) return;
    const timer = window.setTimeout(() => setLayoutCaption(null), 1100);
    return () => window.clearTimeout(timer);
  }, [layoutCaption]);

  const nowMs = useCallback(() => Date.now(), []);
  const focusedId = ws.focusedId;

  const tree = (
    <TooltipProvider delayDuration={350}>
      <div
        // Pinned to the viewport rather than sized in it: in the flow, iPadOS could
        // leave the document scrolled (the header under the browser's chrome, a
        // blank strip under the tab bar); a fixed root gives the page nothing to
        // scroll. The bottom edge stops at an iOS keyboard's overlap (`--kb`), so
        // what is pinned to the bottom — a composer — sits on the keyboard.
        className={cn(
          "fixed inset-x-0 top-0 bottom-[var(--kb,0px)] flex overflow-hidden bg-background text-foreground",
          nav === "sidebar" && (dock === "top" || dock === "bottom") && "flex-col",
        )}
        // With tabs the bar under the content owns the home-indicator inset, so the
        // views above it (the board list, the agent composer) stop padding for it.
        style={nav === "tabs" ? ({ "--safe-bottom": "0px" } as React.CSSProperties) : undefined}
      >
        {/* A desktop keeps the sidebar; a phone or an iPad either way up gets the tab
            bar instead, because 256px of navigation beside a board on a tablet
            leaves the board a column and a half, and a drawer is two taps a switch. */}
        {nav === "sidebar" && !navHidden ? (
          <BoardSidebar
            boards={boards}
            view={view}
            now={now}
            live={live}
            onSelect={go}
            onCreateBoard={() => setBoardDialogOpen(true)}
            onArchiveBoard={(detail) => void setBoardArchived(detail, true)}
            projectCount={projects.length}
            waitingAgents={waitingAgents}
            awaitingAnswer={waiting.enabled ? waiting.total : undefined}
            reviewCount={waiting.enabled ? waiting.reviews.length : undefined}
            awaitingByBoard={waiting.byBoard}
            dock={dock}
            onSearch={() => setPaletteOpen(true)}
            className={dock === "bottom" ? "order-last" : undefined}
            windows={{
              count: windowCount(ws),
              menu: windowsMenu,
              onOpen: (target, where) =>
                where === "here"
                  ? goHere(target)
                  : apply((current) =>
                      openView(current, target, { kind: "split", targetId: current.focusedId, zone: where === "right" ? "right" : "bottom" }),
                    ),
              onHide: toggleNav,
              onDockDragStart: startDockDrag,
              layouts: { count: layouts.length, activeName: activeLayout?.name ?? null, menu: layoutsMenu },
            }}
          />
        ) : null}

        <main className="flex min-h-0 min-w-0 flex-1 flex-col">
          {nav === "tabs" && !inAgentChat ? (
            <TopBar
              title={view.kind === "agents" ? "Agents" : viewTitle(view, allBoards, agents)}
              live={live}
              board={activeBoard}
              boards={boards}
              now={now}
              onBack={view.kind === "board" ? backToBoards : undefined}
              onSwitchBoard={(boardId) => go({ kind: "board", boardId })}
              onAllBoards={backToBoards}
              onSearch={() => setPaletteOpen(true)}
              actions={topBarActions}
            />
          ) : null}
          {error ? (
            <div className="flex items-center gap-3 border-b border-destructive/30 bg-destructive/10 px-5 py-2.5 text-[13px]">
              <ServerCrash className="size-4 shrink-0 text-destructive" />
              <span className="flex-1">{error}</span>
              <Button variant="outline" size="xs" onClick={() => void refresh()}>
                Retry
              </Button>
            </div>
          ) : null}

          <ShellContext.Provider value={shell}>
            {nav === "sidebar" ? (
              <WorkspaceView
                ws={ws}
                apply={apply}
                titleOf={titleOf}
                iconOf={viewIcon}
                now={nowMs}
                renderWindow={(window, _focused, active) => (
                  <ViewPane
                    view={window.view}
                    go={goFor(window.id)}
                    windowed
                    onScreen={active && window.id === requestWindow}
                    shownAgentId={window.view.kind === "agents" && !window.view.agentId ? agentChatNear(ws, window.id) : null}
                  />
                )}
              />
            ) : (
              // The tab layout is the focused window alone, with no chrome.
              <ViewPane key={focusedId} view={view} go={goFor(focusedId)} windowed={false} onScreen />
            )}
          </ShellContext.Provider>

          {/* Kept mounted while hidden, so an open tray survives a keystroke. Out of the
              way while the keyboard is up — it would otherwise ride up on top of it —
              and inside a phone's agent chat. */}
          {nav === "tabs" ? (
            <div className={keyboardOpen || inAgentChat ? "hidden" : "contents"}>
              <BottomTabs
                boards={boards}
                view={view}
                lastBoardId={lastBoardId && boards.some((detail) => detail.board.id === lastBoardId) ? lastBoardId : null}
                onSelect={go}
                onConnectClaude={activeBoard ? () => setConnectRequest((count) => count + 1) : undefined}
                projectCount={projects.length}
                waitingAgents={waitingAgents}
                awaitingAnswer={waiting.enabled ? waiting.total : undefined}
                reviewCount={waiting.enabled ? waiting.reviews.length : undefined}
                onSearch={() => setPaletteOpen(true)}
              />
            </div>
          ) : null}
        </main>

        {/* The navigation hidden: one button in the dock's corner always brings it back. */}
        {navHidden ? (
          <div className={cn("fixed bottom-3 z-40 flex gap-1 rounded-lg border bg-surface/95 p-1 shadow-lg backdrop-blur", dock === "right" ? "right-3" : "left-3")}>
            {windowsMenu(
              <Button variant="ghost" size="sm" aria-label={`Windows · ${windowCount(ws)} open`}>
                <AppWindow /> Windows
              </Button>,
              "top",
            )}
            <Hint label="Show navigation ( [ )">
              <Button variant="ghost" size="icon-sm" aria-label="Show navigation" onClick={toggleNav}>
                <Eye />
              </Button>
            </Hint>
          </div>
        ) : null}
        {layoutCaption ? (
          <div
            key={layoutCaption.at}
            role="status"
            aria-live="polite"
            data-state="open"
            className="motion-fade pointer-events-none fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground/85 px-3.5 py-1.5 text-[13px] font-medium text-background shadow-lg"
          >
            {layoutCaption.name}
          </div>
        ) : null}
        {dockPreview ? (
          <div
            aria-hidden
            className={cn(
              "pointer-events-none fixed z-50 border-2 border-primary/70 bg-primary/15",
              dockPreview === "left" && "inset-y-0 left-0 w-64",
              dockPreview === "right" && "inset-y-0 right-0 w-64",
              dockPreview === "top" && "inset-x-0 top-0 h-12",
              dockPreview === "bottom" && "inset-x-0 bottom-0 h-12",
            )}
          />
        ) : null}
      </div>

      <TaskDialog
        taskId={openTaskId}
        boards={allBoards}
        users={users}
        projects={projects}
        agents={agents}
        onClose={() => setOpenTaskId(null)}
        onChanged={() => void refresh()}
        onError={fail}
        onOpenAgent={(agentId, messageId) => {
          setOpenTaskId(null);
          openAgent(agentId, messageId);
        }}
        onOpenBoard={(boardId) => {
          setOpenTaskId(null);
          go({ kind: "board", boardId });
        }}
      />

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        boards={allBoards}
        agents={agents}
        now={now}
        canAwait={waiting.enabled}
        canSeeAgents={canManageAgents}
        canBeside={nav === "sidebar"}
        actions={paletteActions}
        onGo={paletteGo}
        onOpenTask={setOpenTaskId}
      />

      <CreateBoardDialog
        projects={projects}
        open={boardDialogOpen}
        onOpenChange={setBoardDialogOpen}
        onCreated={(boardId) => {
          void refresh();
          go({ kind: "board", boardId });
          toast.success("Board created");
        }}
      />

      {/* At the top with tabs, where a toast cannot land on the bar or the composer. */}
      <Toaster theme={isDarkTheme(theme) ? "dark" : "light"} position={nav === "tabs" ? "top-center" : "bottom-right"} richColors closeButton />
    </TooltipProvider>
  );

  return <WaitingContext.Provider value={waiting}>{tree}</WaitingContext.Provider>;
}
